(() => {
  const productCache = new Map();

  const formatMoney = (amountCents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amountCents);
    }

    const currencySymbol = window.cartStrings?.currency || "$";
    return `${currencySymbol}${(amountCents / 100).toFixed(2)}`;
  };

  const stripHtml = (htmlText) => {
    if (!htmlText) return "";
    const temp = document.createElement("div");
    temp.innerHTML = htmlText;
    return (temp.textContent || temp.innerText || "").trim();
  };

  const toPositiveInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const getProductInfoFromNode = (node) => node.closest("product-info");

  const getProductForm = (productInfoElement) =>
    productInfoElement?.querySelector(
      'product-form[data-line-item-addons="true"]',
    );

  const getCurrentVariantPriceCents = (productInfoElement) => {
    const variantJson = productInfoElement
      ?.querySelector("variant-selects [data-selected-variant]")
      ?.textContent?.trim();

    if (!variantJson) return null;

    try {
      const selectedVariant = JSON.parse(variantJson);
      return Number(selectedVariant?.price || 0);
    } catch (error) {
      return null;
    }
  };

  const getSeatHeightFieldset = (productInfoElement) => {
    if (!productInfoElement) return null;

    const fieldsets = Array.from(
      productInfoElement.querySelectorAll("fieldset.product-form__input--pill"),
    );

    return (
      fieldsets.find((fieldset) => {
        const label = fieldset.querySelector(".form__label");
        return /seat\s*height/i.test((label?.textContent || "").trim());
      }) || null
    );
  };

  const shouldShowFootring = (productInfoElement) => {
    const seatHeightFieldset = getSeatHeightFieldset(productInfoElement);
    if (!seatHeightFieldset) return false;

    const seatHeightInputs = Array.from(
      seatHeightFieldset.querySelectorAll('input[type="radio"]'),
    );
    const checkedInput = seatHeightFieldset.querySelector(
      'input[type="radio"]:checked',
    );

    if (!checkedInput || !seatHeightInputs.length) return false;

    const firstInput = seatHeightInputs[0];
    const checkedValue = (checkedInput.value || "").toLowerCase();
    const isFirstOptionSelected = firstInput === checkedInput;
    const explicitlyIncompatible = checkedValue.includes(
      "footring not compatible",
    );

    return !isFirstOptionSelected && !explicitlyIncompatible;
  };

  const updateFootringVisibility = (productInfoElement) => {
    if (!productInfoElement) return;

    const footringGroups = Array.from(
      productInfoElement.querySelectorAll("[data-addon-footring-group]"),
    );
    if (!footringGroups.length) return;

    const canShowFootring = shouldShowFootring(productInfoElement);

    footringGroups.forEach((group) => {
      group.hidden = !canShowFootring;

      if (!canShowFootring) {
        const hasSelection = !!group.querySelector(
          "[data-addon-pill].is-selected",
        );
        if (hasSelection) {
          setGroupSelection(group, null);
        }
      }
    });

    updateAddonRootVisibility(productInfoElement);
  };

  const syncActiveAddonVariantIds = (productInfoElement) => {
    const productForm = getProductForm(productInfoElement);
    if (!productForm) return;

    const selectedAddonVariantIds = Array.from(
      productInfoElement.querySelectorAll(
        "[data-addon-pill].is-selected[data-addon-variant-id]",
      ),
    )
      .map((element) => toPositiveInt(element.dataset.addonVariantId))
      .filter((value) => value !== null);

    productForm.dataset.activeAddonVariantIds = JSON.stringify(
      selectedAddonVariantIds,
    );
  };

  const updateDisplayedPrice = (productInfoElement) => {
    if (!productInfoElement.querySelector("[data-addon-root]")) return;

    const basePriceCents = getCurrentVariantPriceCents(productInfoElement);
    if (basePriceCents === null) return;

    const selectedPills = Array.from(
      productInfoElement.querySelectorAll("[data-addon-pill].is-selected"),
    );

    const addonTotalCents = selectedPills.reduce((sum, pill) => {
      return sum + (toPositiveInt(pill.dataset.addonPriceCents) || 0);
    }, 0);

    const totalPriceCents = basePriceCents + addonTotalCents;
    const sectionId = productInfoElement?.dataset?.section;
    const priceRoot = sectionId
      ? productInfoElement.querySelector(`#price-${sectionId}`)
      : null;

    if (!priceRoot) return;

    const regularPrice = priceRoot.querySelector(
      ".price__regular .price-item--regular",
    );
    const salePrice = priceRoot.querySelector(".price__sale .price-item--sale");

    if (regularPrice) regularPrice.textContent = formatMoney(totalPriceCents);
    if (salePrice) salePrice.textContent = formatMoney(totalPriceCents);

    let addonNote = priceRoot.querySelector(".addon-price-note");
    if (!addonNote) {
      addonNote = document.createElement("p");
      addonNote.className = "addon-price-note caption";
      addonNote.style.marginTop = "0.6rem";
      priceRoot.appendChild(addonNote);
    }

    if (addonTotalCents > 0) {
      addonNote.hidden = false;
      addonNote.textContent = `Selected add-ons: +${formatMoney(addonTotalCents)}`;
    } else {
      addonNote.hidden = true;
      addonNote.textContent = "";
    }
  };

  const updateAddonRootVisibility = (productInfoElement) => {
    if (!productInfoElement) return;

    const addonRoot = productInfoElement.querySelector("[data-addon-root]");
    if (!addonRoot) return;

    const hasVisibleGroup = Array.from(
      addonRoot.querySelectorAll("[data-addon-group]"),
    ).some((group) => !group.hidden);

    addonRoot.hidden = !hasVisibleGroup;
  };

  const hideAddonGroup = (group) => {
    if (!group) return;
    setGroupSelection(group, null);
    group.hidden = true;

    const productInfoElement = getProductInfoFromNode(group);
    if (productInfoElement) {
      updateAddonRootVisibility(productInfoElement);
    }
  };

  const showAddonGroup = (group) => {
    if (!group) return;
    group.hidden = false;

    const productInfoElement = getProductInfoFromNode(group);
    if (productInfoElement) {
      updateAddonRootVisibility(productInfoElement);
    }
  };

  const setGroupSelection = (group, selectedButton = null) => {
    const hiddenInput = group.querySelector("[data-addon-hidden-input]");
    const headrestVariantInput = group.querySelector(
      "[data-headrest-variant-input]",
    );

    group.querySelectorAll("[data-addon-pill]").forEach((button) => {
      const isSelected = button === selectedButton;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });

    if (!selectedButton) {
      if (hiddenInput) hiddenInput.value = "";
      if (headrestVariantInput) headrestVariantInput.value = "";
      return;
    }

    if (hiddenInput) {
      hiddenInput.value = selectedButton.dataset.addonValue || "";
    }

    if (headrestVariantInput) {
      headrestVariantInput.value = selectedButton.dataset.addonVariantId || "";
    }
  };

  const syncAddonState = (productInfoElement) => {
    syncActiveAddonVariantIds(productInfoElement);
    updateDisplayedPrice(productInfoElement);
    updateAddonRootVisibility(productInfoElement);
  };

  const onAddonPillClick = (event) => {
    const button = event.target.closest("[data-addon-pill]");
    if (!button || button.disabled) return;

    const group = button.closest("[data-addon-group]");
    if (!group) return;

    const isAlreadySelected = button.classList.contains("is-selected");
    setGroupSelection(group, isAlreadySelected ? null : button);

    const productInfoElement = getProductInfoFromNode(group);
    if (!productInfoElement) return;
    syncAddonState(productInfoElement);
  };

  const getProductsByIdFromCatalog = async (targetProductId) => {
    const key = String(targetProductId);
    if (productCache.has(key)) return productCache.get(key);

    const promise = (async () => {
      const normalizedTarget = String(targetProductId);

      for (let page = 1; page <= 12; page += 1) {
        try {
          const response = await fetch(`/products.json?limit=250&page=${page}`);
          if (!response.ok) break;

          const payload = await response.json();
          const products = payload?.products || [];
          if (!products.length) break;

          const found = products.find(
            (product) => String(product?.id || "") === normalizedTarget,
          );

          if (found) return found;
        } catch (error) {
          break;
        }
      }

      return null;
    })();

    productCache.set(key, promise);
    return promise;
  };

  const buildImageAddonButton = (
    variant,
    productDescription,
    fallbackImageSrc,
    options = {},
  ) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "product-form__addon-pill";
    button.setAttribute("data-addon-pill", "");
    button.setAttribute("aria-pressed", "false");

    const defaultVariantName =
      variant.public_title && variant.public_title !== "Default Title"
        ? variant.public_title
        : variant.title;
    const priceCents = Math.round(parseFloat(variant.price || "0") * 100);
    const hasTitleOverride = Object.prototype.hasOwnProperty.call(
      options,
      "titleOverride",
    );
    const hasDescriptionOverride = Object.prototype.hasOwnProperty.call(
      options,
      "descriptionOverride",
    );
    const hasValueOverride = Object.prototype.hasOwnProperty.call(
      options,
      "valueOverride",
    );
    const hasPriceOverride = Object.prototype.hasOwnProperty.call(
      options,
      "priceOverride",
    );
    const price = hasPriceOverride
      ? options.priceOverride || ""
      : formatMoney(priceCents);
    const variantName = hasTitleOverride
      ? options.titleOverride || ""
      : defaultVariantName;
    const descriptionText = hasDescriptionOverride
      ? options.descriptionOverride || ""
      : productDescription || "Add-on option";
    const valueText = hasValueOverride
      ? options.valueOverride || ""
      : variantName;

    button.dataset.addonName = variantName;
    button.dataset.addonDescription = descriptionText;
    button.dataset.addonPrice = price;
    button.dataset.addonPriceCents = String(priceCents);
    button.dataset.addonValue = valueText;
    button.dataset.addonVariantId = String(variant.id);

    const imageSrc = variant.featured_image?.src || fallbackImageSrc || null;
    if (imageSrc) {
      button.classList.add("product-form__addon-pill--with-image");
      const img = document.createElement("img");
      img.className = "product-form__addon-pill-image";
      img.src = imageSrc;
      img.alt = variantName;
      img.loading = "lazy";
      img.width = 70;
      img.height = 70;
      button.appendChild(img);
    }

    const textWrap = document.createElement("span");
    textWrap.className = "product-form__addon-pill-text";

    const nameSpan = document.createElement("span");
    nameSpan.className = "product-form__addon-pill-title";
    nameSpan.textContent = variantName;

    const descriptionSpan = document.createElement("span");
    descriptionSpan.className = "product-form__addon-pill-description";
    descriptionSpan.textContent = descriptionText;
    descriptionSpan.hidden = !descriptionText;

    const priceSpan = document.createElement("span");
    priceSpan.className = "product-form__addon-pill-price";
    priceSpan.textContent = price;
    priceSpan.hidden = !price;

    textWrap.appendChild(nameSpan);
    textWrap.appendChild(descriptionSpan);
    textWrap.appendChild(priceSpan);
    button.appendChild(textWrap);

    if (variant.available === false) {
      button.disabled = true;
      button.classList.add("is-disabled");
    }

    return button;
  };

  const getPreferredVariant = (variants, preferredVariantId = null) => {
    if (!Array.isArray(variants) || !variants.length) return null;

    const normalizedPreferredVariantId = toPositiveInt(preferredVariantId);
    if (normalizedPreferredVariantId) {
      const matchedVariant = variants.find(
        (variant) =>
          toPositiveInt(variant?.id) === normalizedPreferredVariantId,
      );
      if (matchedVariant) return matchedVariant;
    }

    return (
      variants.find((variant) => variant?.available) || variants[0] || null
    );
  };

  const loadHeadrestGroup = async (group) => {
    if (!group || group.dataset.loaded === "true") return;

    const headrestProductId = group.dataset.headrestProductId;
    const optionsContainer = group.querySelector("[data-headrest-options]");
    if (!headrestProductId || !optionsContainer) return;

    group.dataset.loaded = "pending";
    optionsContainer.innerHTML =
      '<span class="product-form__addon-loading">Loading headrest options...</span>';

    const product = await getProductsByIdFromCatalog(headrestProductId);
    if (!product) {
      group.dataset.loaded = "false";
      hideAddonGroup(group);
      return;
    }

    const productDescription = stripHtml(product.body_html).slice(0, 120);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const fallbackImageSrc =
      Array.isArray(product.images) && product.images.length
        ? product.images[0].src
        : null;

    optionsContainer.innerHTML = "";
    variants.forEach((variant) => {
      optionsContainer.appendChild(
        buildImageAddonButton(variant, productDescription, fallbackImageSrc, {
          titleOverride: group.dataset.headrestLabel || "Headrest",
          descriptionOverride: group.dataset.headrestDescription || "",
          valueOverride: group.dataset.headrestLabel || "Headrest",
        }),
      );
    });

    const defaultVariantId = toPositiveInt(
      group.dataset.headrestDefaultVariantId,
    );
    if (defaultVariantId) {
      const defaultButton = optionsContainer.querySelector(
        `[data-addon-pill][data-addon-variant-id="${defaultVariantId}"]`,
      );
      if (defaultButton) {
        setGroupSelection(group, defaultButton);
      }
    }

    if (!variants.length) {
      group.dataset.loaded = "false";
      hideAddonGroup(group);
      return;
    }

    group.dataset.loaded = "true";
    showAddonGroup(group);

    const productInfoElement = getProductInfoFromNode(group);
    if (productInfoElement) {
      syncAddonState(productInfoElement);
    }
  };

  const loadFootringGroup = async (group) => {
    if (!group || group.dataset.loaded === "true") return;

    const footringProductId = group.dataset.footringProductId;
    const preferredVariantId = group.dataset.footringVariantId;
    const optionsContainer = group.querySelector("[data-footring-options]");
    if (!footringProductId) {
      hideAddonGroup(group);
      group.dataset.loaded = "false";
      return;
    }

    if (!optionsContainer) {
      group.dataset.loaded = "false";
      hideAddonGroup(group);
      return;
    }

    group.dataset.loaded = "pending";
    optionsContainer.innerHTML =
      '<span class="product-form__addon-loading">Loading footring options...</span>';

    const product = await getProductsByIdFromCatalog(footringProductId);
    if (!product) {
      group.dataset.loaded = "false";
      hideAddonGroup(group);
      return;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = getPreferredVariant(variants, preferredVariantId);
    if (!variant || variant.available === false) {
      group.dataset.loaded = "false";
      hideAddonGroup(group);
      return;
    }

    const fallbackImageSrc =
      Array.isArray(product.images) && product.images.length
        ? product.images[0].src
        : null;
    const productDescription = stripHtml(product.body_html).slice(0, 120);

    const footringButton = buildImageAddonButton(
      variant,
      productDescription,
      fallbackImageSrc,
      {
        titleOverride: "Footring",
        valueOverride: "Footring",
        descriptionOverride: "Footring",
      },
    );

    optionsContainer.innerHTML = "";
    optionsContainer.appendChild(footringButton);

    group.dataset.loaded = "true";

    const productInfoElement = getProductInfoFromNode(group);
    if (productInfoElement) {
      syncAddonState(productInfoElement);
    }
  };

  const loadGlideOption = async (group) => {
    if (!group || group.dataset.glideLoaded === "true") return;

    const glideProductId = group.dataset.glideProductId;
    const optionsContainer = group.querySelector("[data-glide-options]");
    if (!glideProductId || !optionsContainer) return;

    group.dataset.glideLoaded = "pending";

    const product = await getProductsByIdFromCatalog(glideProductId);
    if (!product) {
      group.dataset.glideLoaded = "false";
      return;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) {
      group.dataset.glideLoaded = "false";
      return;
    }

    const fallbackImageSrc =
      Array.isArray(product.images) && product.images.length
        ? product.images[0].src
        : null;
    const productDescription = stripHtml(product.body_html).slice(0, 120);
    const shouldUseGlideOverrides = variants.length === 1;

    optionsContainer
      .querySelectorAll("[data-addon-pill][data-addon-source='glide']")
      .forEach((button) => button.remove());

    variants.forEach((variant) => {
      const glideButton = buildImageAddonButton(
        variant,
        productDescription,
        fallbackImageSrc,
        {
          ...(shouldUseGlideOverrides
            ? {
                titleOverride: group.dataset.glideLabel || "Glide",
                descriptionOverride: group.dataset.glideDescription || "",
                valueOverride:
                  group.dataset.glideValue ||
                  group.dataset.glideDescription ||
                  group.dataset.glideLabel ||
                  "Glide",
                priceOverride: group.dataset.glidePrice || "",
              }
            : {}),
        },
      );

      glideButton.dataset.addonSource = "glide";
      optionsContainer.appendChild(glideButton);
    });

    group.dataset.glideLoaded = "true";

    const productInfoElement = getProductInfoFromNode(group);
    if (productInfoElement) {
      syncAddonState(productInfoElement);
    }
  };

  const initializeProductInfo = (productInfoElement) => {
    if (!productInfoElement) return;

    updateFootringVisibility(productInfoElement);

    productInfoElement
      .querySelectorAll("[data-addon-footring-group]")
      .forEach((group) => {
        loadFootringGroup(group);
      });

    productInfoElement
      .querySelectorAll("[data-headrest-group]")
      .forEach((group) => {
        loadHeadrestGroup(group);
      });

    productInfoElement
      .querySelectorAll("[data-glide-group]")
      .forEach((group) => {
        loadGlideOption(group);
      });

    syncAddonState(productInfoElement);
  };

  const initializeAll = () => {
    document
      .querySelectorAll("product-info")
      .forEach((productInfoElement) =>
        initializeProductInfo(productInfoElement),
      );
  };

  const handleVariantChangePubSub = (eventPayload) => {
    const sectionId = eventPayload?.data?.sectionId;
    if (!sectionId) return;

    const productInfoElement = document.querySelector(
      `product-info[data-section="${sectionId}"]`,
    );
    if (!productInfoElement) return;

    window.requestAnimationFrame(() =>
      initializeProductInfo(productInfoElement),
    );
  };

  const onVariantOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== "radio") return;

    const productInfoElement = getProductInfoFromNode(input);
    if (!productInfoElement) return;

    updateFootringVisibility(productInfoElement);
    syncAddonState(productInfoElement);
  };

  document.addEventListener("click", onAddonPillClick);
  document.addEventListener("change", onVariantOptionChange);
  document.addEventListener("DOMContentLoaded", initializeAll);
  document.addEventListener("shopify:section:load", initializeAll);

  if (
    typeof subscribe === "function" &&
    typeof PUB_SUB_EVENTS !== "undefined" &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChangePubSub);
  }
})();
